import { useState } from 'react';
import { AlertTriangle, Download, FileSpreadsheet, XCircle } from 'lucide-react';
import { FileDropzone } from './FileDropzone';
import { useCommitImport, usePreviewImport } from '../../hooks/useOnboarding';
import {
  downloadTemplate,
  ImportApiError,
  type ImportCommitResult,
  type ImportPreview,
  type ImportRowError,
} from '../../lib/onboardingApi';

// Step 2 — Excel: download the template, upload a filled workbook, see
// row-level validation (red errors, yellow warnings), then import when clean.

function EntityCounts({ preview }: { preview: ImportPreview }) {
  const rows: { label: string; n: number }[] = [
    { label: 'Clienți', n: preview.clients.length },
    { label: 'Echipă', n: preview.team.length },
    { label: 'Servicii', n: preview.services.length },
    { label: 'Proiecte', n: preview.projects.length },
    { label: 'Alocări', n: preview.allocations.length },
  ];
  return (
    <div className="flex flex-wrap gap-2">
      {rows.map((r) => (
        <span
          key={r.label}
          className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-700"
        >
          {r.label}: {r.n}
        </span>
      ))}
    </div>
  );
}

function ErrorsTable({ errors }: { errors: ImportRowError[] }) {
  return (
    <div className="overflow-hidden rounded-md border border-red-200">
      <table className="min-w-full divide-y divide-red-100 text-sm">
        <thead className="bg-red-50 text-left text-xs font-semibold uppercase tracking-wide text-red-700">
          <tr>
            <th className="px-3 py-2">Foaie</th>
            <th className="px-3 py-2">Rând</th>
            <th className="px-3 py-2">Coloană</th>
            <th className="px-3 py-2">Eroare</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-red-50 bg-white">
          {errors.map((e, i) => (
            <tr key={i} className="text-red-800">
              <td className="px-3 py-2 font-medium">{e.sheet}</td>
              <td className="px-3 py-2">{e.row}</td>
              <td className="px-3 py-2">{e.column ?? '—'}</td>
              <td className="px-3 py-2">{e.message}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatsSummary({ result }: { result: ImportCommitResult }) {
  const rows: { label: string; key: keyof ImportCommitResult['stats'] }[] = [
    { label: 'Clienți', key: 'clients' },
    { label: 'Echipă', key: 'team' },
    { label: 'Servicii', key: 'services' },
    { label: 'Proiecte', key: 'projects' },
    { label: 'Alocări', key: 'allocations' },
  ];
  return (
    <div className="rounded-md border border-green-200 bg-green-50 p-4">
      <p className="text-sm font-semibold text-green-800">Import finalizat cu succes.</p>
      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-5">
        {rows.map((r) => {
          const s = result.stats[r.key];
          return (
            <div
              key={r.key}
              className="rounded border border-green-200 bg-white px-3 py-2 text-center"
            >
              <p className="text-xs text-gray-500">{r.label}</p>
              <p className="text-sm font-semibold text-gray-900">
                +{s.created} / ↻{s.updated}
              </p>
            </div>
          );
        })}
      </div>
      {result.warnings.length > 0 && (
        <ul className="mt-3 list-inside list-disc text-xs text-amber-700">
          {result.warnings.map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function StepExcel() {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  // Errors surfaced by a rejected commit (422) when the server re-validation
  // disagrees with the last preview.
  const [commitErrors, setCommitErrors] = useState<ImportRowError[] | null>(null);
  const [commitResult, setCommitResult] = useState<ImportCommitResult | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const previewMutation = usePreviewImport();
  const commitMutation = useCommitImport();

  function handleFile(f: File) {
    setFile(f);
    setPreview(null);
    setCommitErrors(null);
    setCommitResult(null);
    previewMutation.mutate(f, { onSuccess: (data) => setPreview(data) });
  }

  function handleCommit() {
    if (!file) return;
    setCommitErrors(null);
    setCommitResult(null);
    commitMutation.mutate(file, {
      onSuccess: (res) => setCommitResult(res),
      onError: (err) => {
        if (err instanceof ImportApiError && err.details) {
          setCommitErrors(err.details);
        }
      },
    });
  }

  async function handleDownload() {
    setDownloadError(null);
    try {
      await downloadTemplate();
    } catch {
      setDownloadError('Șablonul nu a putut fi descărcat. Încercați din nou.');
    }
  }

  const errors = commitErrors ?? preview?.errors ?? [];
  const warnings = preview?.warnings ?? [];
  const canImport = Boolean(preview?.valid) && !commitResult;

  return (
    <div className="space-y-5">
      <section className="rounded-lg border border-gray-200 bg-white p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="rounded-md bg-brand-50 p-2 text-brand-600">
              <FileSpreadsheet className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Import din Excel</h3>
              <p className="mt-1 text-sm text-gray-500">
                Descărcați șablonul, completați-l și încărcați-l. Validăm fiecare rând înainte de
                import.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => void handleDownload()}
            className="inline-flex items-center gap-2 rounded-md border border-brand-300 bg-white px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50"
          >
            <Download className="h-4 w-4" />
            Descarcă șablon
          </button>
        </div>
        {downloadError && <p className="mt-2 text-sm text-red-600">{downloadError}</p>}
      </section>

      <FileDropzone
        accept=".xlsx"
        hint="Doar fișiere .xlsx (șablonul completat)"
        onFile={handleFile}
        busyFile={previewMutation.isPending ? file : null}
        busyLabel="Se validează fișierul…"
      />

      {file && !previewMutation.isPending && (
        <p className="text-sm text-gray-600">
          Fișier selectat: <span className="font-medium text-gray-900">{file.name}</span>
        </p>
      )}
      {previewMutation.isError && (
        <p className="text-sm text-red-600">
          {previewMutation.error instanceof Error
            ? previewMutation.error.message
            : 'Fișierul nu a putut fi citit.'}
        </p>
      )}

      {preview && !commitResult && (
        <div className="space-y-3">
          <EntityCounts preview={preview} />

          {warnings.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
              <p className="flex items-center gap-2 text-sm font-semibold text-amber-800">
                <AlertTriangle className="h-4 w-4" />
                Avertismente ({warnings.length})
              </p>
              <ul className="mt-2 list-inside list-disc text-sm text-amber-700">
                {warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          {errors.length > 0 ? (
            <div className="space-y-2">
              <p className="flex items-center gap-2 text-sm font-semibold text-red-700">
                <XCircle className="h-4 w-4" />
                Erori de validare ({errors.length}) — corectați-le și încărcați din nou
              </p>
              <ErrorsTable errors={errors} />
            </div>
          ) : (
            <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
              Fișierul este valid și gata de import.
            </div>
          )}

          <button
            type="button"
            onClick={handleCommit}
            disabled={!canImport || commitMutation.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {commitMutation.isPending && (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            )}
            Importă
          </button>
          {commitMutation.isError && !commitErrors && (
            <p className="text-sm text-red-600">
              {commitMutation.error instanceof Error
                ? commitMutation.error.message
                : 'Importul a eșuat.'}
            </p>
          )}
        </div>
      )}

      {commitResult && <StatsSummary result={commitResult} />}
    </div>
  );
}
